import React, { useState, useEffect } from "react";
import { Table, Container, Row, Col, Pagination } from "react-bootstrap";
import { FaChevronDown, FaChevronUp, FaSort, FaSortUp, FaSortDown } from "react-icons/fa";
import Button, { BUTTON_TYPES } from '../../components/Button';
import SearchInput from '../../components/inputs/SearchInput';
import "../../styles/AdminPage.css";
import Page from "../../components/Page";
import { useNavigate, useLocation } from 'react-router-dom';
import FilterButton from "../../components/FilterButton";
import EditButton from "../../components/EditButton";
import MemberEditModal from "../../components/modals/MemberEditModal";
import MemberFilterModal from "../../components/modals/MemberFilterModal";
import GenerateReportModal from "../../components/modals/GenerateReportModal";
import FilterTag from "../../components/FilterTag";
import ErrorPage from "../ErrorPage";
import { useAdmin } from "./AdminContext";
import { getAllCommittees, getAllMembersIncludingArchived } from "../../api/admin/AdminDashboardAPI";

export default function MemberSearchPage() {
    const [isArchive, setIsArchive] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const [eventFilter, setEventFilter] = useState(location.state?.selectedEvent);
    const [committeeFilter, setCommitteeFilter] = useState(location.state?.selectedCommittee);
    const selectedMemberEventType = location.state?.selectedMemberType;
    const [filterTags, setFilterTags] = useState([]);

    const selectedEvent = location.state?.selectedEvent;

    const { members, committees, updateMembers } = useAdmin();

    useEffect(() => {
        setEventFilter(location.state?.selectedEvent);
        setCommitteeFilter(location.state?.selectedCommittee);
    }, [location.state]);

    const clearLocationState = () => {
        navigate(location.pathname, { replace: true, state: {} });
    };

    const goToEvents = (member, memberEventType) => {
        navigate('/admin/events', { state: { selectedMember: member, selectedMemberEventType: memberEventType } });
    };

    const [selectedMember, setSelectedMember] = useState(null);

    const [appliedFilters, setAppliedFilters] = useState({});

    const [activeModal, setActiveModal] = useState(null);

    const [modalFormData, setModalFormData] = useState({
        generate: {
            selectedReportType: 'none',
            selectedFileType: 'none',
            reportStartDate: "",
            reportEndDate: "",
        }
    });

    const openModal = (modalType) => {
        setActiveModal(modalType);
    };

    const closeModal = () => {
        setActiveModal(null);
    };

    const handleFilterApply = async (data) => {
        setAppliedFilters(data);
    };

    const activeStatusTypes = [
        { id: 1, activeStatusType: 'Active' },
        { id: 2, activeStatusType: 'Inactive' }
    ];

    const currentYear = new Date().getFullYear();

    const gradYearTypes = Array.from({ length: 6 }, (_, i) => ({
        id: i + 1,
        gradYearType: (currentYear + i).toString()
    }));

    const defaultFilters = {
        activeSemestersRange: { min: 0, max: 16 },
        generalMeetingsRange: { min: 0, max: 200 },
        committeeMeetingsRange: { min: 0, max: 200 },
        volunteerEventsRange: { min: 0, max: 200 },
        socialEventsRange: { min: 0, max: 200 },
        selectedActiveStatusTypeIDs: [],
        selectedGradYearTypeIDs: [],
        selectedCommitteeTypeIDs: [],
        isArchiveEnabled: false
    };

    useEffect(() => {
        setFilterTags([]);
        if (
            appliedFilters.isArchiveEnabled
        ) {
            setIsArchive(true);
            setFilterTags(prevTags => [...prevTags, {
                field: 'isArchiveEnabled',
                label: `Archived Events Included`
            }]);
        }

        if (eventFilter) {
            setFilterTags(prevTags => [...prevTags, {
                field: 'eventAttendance',
                label: `${eventFilter.eventName} Attendance`
            }]);
        }

        if (committeeFilter && selectedMemberEventType === "Active Members") {
            setFilterTags(prevTags => [...prevTags, {
                field: 'committeeMembers',
                label: `${committeeFilter.committeeName} ${selectedMemberEventType}`
            }]);
        }

        if (
            appliedFilters.selectedActiveStatusTypeIDs &&
            appliedFilters.selectedActiveStatusTypeIDs.length > defaultFilters.selectedActiveStatusTypeIDs.length
        ) {
            appliedFilters.selectedActiveStatusTypeIDs.forEach(id => {
                const rec = activeStatusTypes.find(as => as.id === id);
                if (rec) {
                    setFilterTags(prevTags => [...prevTags, {
                        field: 'selectedActiveStatusTypeIDs',
                        id,
                        label: `Status: ${rec.activeStatusType}`
                    }]);
                }
            });
        }

        if (
            appliedFilters.selectedGradYearTypeIDs &&
            appliedFilters.selectedGradYearTypeIDs.length > defaultFilters.selectedGradYearTypeIDs.length
        ) {
            appliedFilters.selectedGradYearTypeIDs.forEach(id => {
                const rec = gradYearTypes.find(gy => gy.id === id);
                if (rec) {
                    setFilterTags(prevTags => [...prevTags, {
                        field: 'selectedGradYearTypeIDs',
                        id,
                        label: `Grad Year: ${rec.gradYearType}`
                    }]);
                }
            });
        }

        if (
            (appliedFilters.selectedCommitteeTypeIDs &&
                appliedFilters.selectedCommitteeTypeIDs.length > defaultFilters.selectedCommitteeTypeIDs.length) || (committeeFilter && selectedMemberEventType === "Members")
        ) {
            if (committeeFilter && !appliedFilters.selectedCommitteeTypeIDs) {
                appliedFilters.selectedCommitteeTypeIDs = [committeeFilter.id];
            }
            appliedFilters.selectedCommitteeTypeIDs.forEach(id => {
                if (id === 0) {
                    setFilterTags(prevTags => [...prevTags, {
                        field: 'selectedCommitteeTypeIDs',
                        id,
                        label: `Committee: None`
                    }]);
                } else {
                    const rec = committees.find(ct => ct.id === id);
                    if (rec) {
                        setFilterTags(prevTags => [...prevTags, {
                            field: 'selectedCommitteeTypeIDs',
                            id,
                            label: `${rec.committeeName} Members`
                        }]);
                    }
                }
            });
        }

        if (appliedFilters.activeSemestersRange &&
            (appliedFilters.activeSemestersRange.min !== defaultFilters.activeSemestersRange.min ||
                appliedFilters.activeSemestersRange.max !== defaultFilters.activeSemestersRange.max)) {
            setFilterTags(prevTags => [...prevTags, {
                field: 'activeSemestersRange',
                label: `Active Semesters: ${appliedFilters.activeSemestersRange.min} - ${appliedFilters.activeSemestersRange.max}`
            }]);
        }

        if (appliedFilters.generalMeetingsRange &&
            (appliedFilters.generalMeetingsRange.min !== defaultFilters.generalMeetingsRange.min ||
                appliedFilters.generalMeetingsRange.max !== defaultFilters.generalMeetingsRange.max)) {
            setFilterTags(prevTags => [...prevTags, {
                field: 'generalMeetingsRange',
                label: `General Meetings: ${appliedFilters.generalMeetingsRange.min} - ${appliedFilters.generalMeetingsRange.max}`
            }]);
        }

        if (appliedFilters.committeeMeetingsRange &&
            (appliedFilters.committeeMeetingsRange.min !== defaultFilters.committeeMeetingsRange.min ||
                appliedFilters.committeeMeetingsRange.max !== defaultFilters.committeeMeetingsRange.max)) {
            setFilterTags(prevTags => [...prevTags, {
                field: 'committeeMeetingsRange',
                label: `Committee Meetings: ${appliedFilters.committeeMeetingsRange.min} - ${appliedFilters.committeeMeetingsRange.max}`
            }]);
        }

        if (appliedFilters.volunteerEventsRange &&
            (appliedFilters.volunteerEventsRange.min !== defaultFilters.volunteerEventsRange.min ||
                appliedFilters.volunteerEventsRange.max !== defaultFilters.volunteerEventsRange.max)) {
            setFilterTags(prevTags => [...prevTags, {
                field: 'volunteerEventsRange',
                label: `Volunteer Events: ${appliedFilters.volunteerEventsRange.min} - ${appliedFilters.volunteerEventsRange.max}`
            }]);
        }

        if (appliedFilters.socialEventsRange &&
            (appliedFilters.socialEventsRange.min !== defaultFilters.socialEventsRange.min ||
                appliedFilters.socialEventsRange.max !== defaultFilters.socialEventsRange.max)) {
            setFilterTags(prevTags => [...prevTags, {
                field: 'socialEventsRange',
                label: `Social Events: ${appliedFilters.socialEventsRange.min} - ${appliedFilters.socialEventsRange.max}`
            }]);
        }
    }, [appliedFilters]);

    const removeFilter = (field, id) => {
        switch (field) {
            case ('selectedActiveStatusTypeIDs'):
                setAppliedFilters(prev => ({
                    ...prev,
                    selectedActiveStatusTypeIDs: prev.selectedActiveStatusTypeIDs.filter(item => item !== id)
                }));
                setModalFormData(prev => ({
                    ...prev,
                    create: {
                        ...prev.create,
                        selectedActiveStatusTypeIDs: prev.create.selectedActiveStatusTypeIDs.filter(item => item !== id)
                    }
                }));
                break;
            case ('selectedGradYearTypeIDs'):
                setAppliedFilters(prev => ({
                    ...prev,
                    selectedGradYearTypeIDs: prev.selectedGradYearTypeIDs.filter(item => item !== id)
                }));
                setModalFormData(prev => ({
                    ...prev,
                    create: {
                        ...prev.create,
                        selectedGradYearTypeIDs: prev.create.selectedGradYearTypeIDs.filter(item => item !== id)
                    }
                }));
                break;
            case ('selectedCommitteeTypeIDs'):
                if (committeeFilter && selectedMemberEventType === "Members") {
                    setCommitteeFilter(null);
                    clearLocationState();
                    setAppliedFilters(prev => ({
                        ...prev,
                        committeeMembers: null,
                        selectedCommitteeTypeIDs: prev.selectedCommitteeTypeIDs.filter(item => item !== committeeFilter.id)
                    }));
                    break;
                }
                setAppliedFilters(prev => ({
                    ...prev,
                    selectedCommitteeTypeIDs: prev.selectedCommitteeTypeIDs.filter(item => item !== id)
                }));
                setModalFormData(prev => ({
                    ...prev,
                    create: {
                        ...prev.create,
                        selectedCommitteeTypeIDs: prev.create.selectedCommitteeTypeIDs.filter(item => item !== id)
                    }
                }));
                break;
            case ('activeSemestersRange'):
                setAppliedFilters(prev => ({ ...prev, activeSemestersRange: defaultFilters.activeSemestersRange }));
                setModalFormData(prev => ({
                    ...prev,
                    create: { ...prev.create, activeSemestersRange: defaultFilters.activeSemestersRange }
                }));
                break;
            case ('generalMeetingsRange'):
                setAppliedFilters(prev => ({ ...prev, generalMeetingsRange: defaultFilters.generalMeetingsRange }));
                setModalFormData(prev => ({
                    ...prev,
                    create: { ...prev.create, generalMeetingsRange: defaultFilters.generalMeetingsRange }
                }));
                break;
            case ('committeeMeetingsRange'):
                setAppliedFilters(prev => ({ ...prev, committeeMeetingsRange: defaultFilters.committeeMeetingsRange }));
                setModalFormData(prev => ({
                    ...prev,
                    create: { ...prev.create, committeeMeetingsRange: defaultFilters.committeeMeetingsRange }
                }));
                break;
            case ('volunteerEventsRange'):
                setAppliedFilters(prev => ({ ...prev, volunteerEventsRange: defaultFilters.volunteerEventsRange }));
                setModalFormData(prev => ({
                    ...prev,
                    create: { ...prev.create, volunteerEventsRange: defaultFilters.volunteerEventsRange }
                }));
                break;
            case ('socialEventsRange'):
                setAppliedFilters(prev => ({ ...prev, socialEventsRange: defaultFilters.socialEventsRange }));
                setModalFormData(prev => ({
                    ...prev,
                    create: { ...prev.create, socialEventsRange: defaultFilters.socialEventsRange }
                }));
                break;
            case ('eventAttendance'):
                setEventFilter(null);
                clearLocationState();
                setAppliedFilters(prev => ({
                    ...prev,
                    eventAttendance: null,
                }));
                break;
            case ('committeeMembers'):
                if (selectedMemberEventType === "Active Members") {
                    setCommitteeFilter(null);
                    clearLocationState();
                    setAppliedFilters(prev => ({
                        ...prev,
                        committeeMembers: null,
                    }));
                    break;
                }
            case 'isArchiveEnabled':
                const updatedArchive = {
                    ...appliedFilters,
                    isArchiveEnabled: false
                };

                setAppliedFilters(updatedArchive);
                setIsArchive(false);
                setModalFormData(prev => ({
                    ...prev,
                    create: {
                        ...prev.create,
                        isArchiveEnabled: defaultFilters.isArchiveEnabled
                    }
                }));

                handleFilterApply(updatedArchive);
                break;
        }
    };

    useEffect(() => {
        const sortedMembers = [...members].sort((a, b) => {
            const nameA = a.firstName || "";
            const nameB = b.firstName || "";
            return nameA.localeCompare(nameB);
        });
        setDisplayMembers(sortedMembers);
    }, [members]);

    const [displayMembers, setDisplayMembers] = useState(members.sort((a, b) => {
        const nameA = a.firstName || "";
        const nameB = b.firstName || "";
        return nameA.localeCompare(nameB);
    }));
    const [expandedRows, setExpandedRows] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [sortConfig, setSortConfig] = useState({ key: 'firstName', direction: 'ascending' });
    const [searchQuery, setSearchQuery] = useState('');
    const itemsPerPage = 25;

    const search = () => {
        const lowerSearchQuery = searchQuery.toLowerCase().trim();
        let filteredMembers;

        if (!lowerSearchQuery) {
            filteredMembers = [...members];
        } else {
            filteredMembers = members.filter(member =>
                member.firstName.toLowerCase().includes(lowerSearchQuery) ||
                member.ritEmail.toLowerCase().includes(lowerSearchQuery)
            );
        }

        if (filterTags && filterTags.length > 0) {



            filteredMembers = filteredMembers.filter(member => {
                // group filters by field
                const filtersByField = filterTags.reduce((acc, filter) => {
                    if (!acc[filter.field]) {
                        acc[filter.field] = [];
                    }
                    acc[filter.field].push(filter);
                    return acc;
                }, {});

                // check if member satisfies filters from all fields
                for (const fieldName in filtersByField) {
                    const fieldFilters = filtersByField[fieldName];
                    const fieldMatches = fieldFilters.some(filter => {
                        switch (filter.field) {
                            case 'eventAttendance':
                                return selectedEvent.membersAttended.some(eventMember => eventMember.id === member.id);
                            case 'committeeMembers':
                                const isActiveFilter = filter.label.endsWith("Active Members");
                                const expectedCommitteeName = isActiveFilter
                                    ? filter.label.replace(" Active Members", "")
                                    : filter.label.replace(" Members", "");
                                const inCommittee = member.memberOfCommittees &&
                                    member.memberOfCommittees.some(committeeName => committeeName === expectedCommitteeName);
                                return isActiveFilter ? inCommittee && member.status === "Active" : inCommittee;
                            case 'selectedActiveStatusTypeIDs': {
                                const expectedStatus = filter.label.split(': ')[1];
                                return member.status == expectedStatus;
                            }
                            case 'selectedGradYearTypeIDs':
                                const expectedGradYear = filter.label.split(': ')[1];
                                return member.gradYear == expectedGradYear;
                            case 'selectedCommitteeTypeIDs':
                                if (filter.label.trim() === "Committee: None") {
                                    return !member.memberOfCommittees || member.memberOfCommittees.length === 0;
                                }
                                const expectedCommitteeIDName = filter.label.replace(" Members", "");
                                const inCommitteeID = member.memberOfCommittees &&
                                    member.memberOfCommittees.some(committeeName => committeeName === expectedCommitteeIDName);
                                return inCommitteeID;
                            case 'activeSemestersRange':
                                const activeRangePart = filter.label.split(': ')[1];
                                const [activeMinStr, activeMaxStr] = activeRangePart.split(' - ');
                                const activeMin = parseInt(activeMinStr, 10);
                                const activeMax = parseInt(activeMaxStr, 10);
                                return member.activeMemberSemestersCount >= activeMin && member.activeMemberSemestersCount <= activeMax;
                            case 'generalMeetingsRange':
                                const generalRangePart = filter.label.split(': ')[1];
                                const [generalMinStr, generalMaxStr] = generalRangePart.split(' - ');
                                const generalMin = parseInt(generalMinStr, 10);
                                const generalMax = parseInt(generalMaxStr, 10);
                                return member.eventTypeAttendance["General Meeting"] >= generalMin && member.eventTypeAttendance["General Meeting"] <= generalMax;
                            case 'committeeMeetingsRange':
                                const committeeRangePart = filter.label.split(': ')[1];
                                const [committeeMinStr, committeeMaxStr] = committeeRangePart.split(' - ');
                                const committeeMin = parseInt(committeeMinStr, 10);
                                const committeeMax = parseInt(committeeMaxStr, 10);
                                return member.eventTypeAttendance["Committee Meeting"] >= committeeMin && member.eventTypeAttendance["Committee Meeting"] <= committeeMax;
                            case 'volunteerEventsRange':
                                const volunteerRangePart = filter.label.split(': ')[1];
                                const [volunteerMinStr, volunteerMaxStr] = volunteerRangePart.split(' - ');
                                const volunteerMin = parseInt(volunteerMinStr, 10);
                                const volunteerMax = parseInt(volunteerMaxStr, 10);
                                return member.eventTypeAttendance["Volunteer Event"] >= volunteerMin && member.eventTypeAttendance["Volunteer Event"] <= volunteerMax;
                            case 'socialEventsRange':
                                const socialRangePart = filter.label.split(': ')[1];
                                const [socialMinStr, socialMaxStr] = socialRangePart.split(' - ');
                                const socialMin = parseInt(socialMinStr, 10);
                                const socialMax = parseInt(socialMaxStr, 10);
                                return member.eventTypeAttendance["Social Event"] >= socialMin && member.eventTypeAttendance["Social Event"] <= socialMax;
                            default:
                                return true;
                        }
                    });

                    // If for any field, none of the filters match, then the member should be excluded
                    if (!fieldMatches) {
                        return false;
                    }
                }

                // If the member has passed the checks for all fields, include them
                return true;
            });
        }

        const sortedMembers = [...filteredMembers].sort((a, b) => {
            const nameA = a.firstName || "";
            const nameB = b.firstName || "";
            return nameA.localeCompare(nameB);
        });

        setDisplayMembers(sortedMembers);
        setSortConfig({ key: 'firstName', direction: 'ascending' });
        setCurrentPage(1);
        setExpandedRows([]);
    }

    useEffect(() => {
        search();
    }, [appliedFilters, members]);

    useEffect(() => {
        search();
    }, [filterTags]);

    const handleRowClick = (index) => {
        const isRowCurrentlyExpanded = expandedRows.includes(index);
        setExpandedRows(isRowCurrentlyExpanded
            ? expandedRows.filter(id => id !== index)
            : [...expandedRows, index]
        );
    };

    const handleSearchChange = (e) => {
        setSearchQuery(e.target.value);
    };

    const handleSearchClick = (e) => {
        e.preventDefault();
        search();
    };

    const indexOfLastMember = currentPage * itemsPerPage;
    const indexOfFirstMember = indexOfLastMember - itemsPerPage;
    const currentMembers = displayMembers.slice(indexOfFirstMember, indexOfLastMember);

    const handleSort = (key) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        const sortedData = [...displayMembers].sort((a, b) => {
            if (a[key] < b[key]) return direction === 'ascending' ? -1 : 1;
            if (a[key] > b[key]) return direction === 'ascending' ? 1 : -1;
            return 0;
        });
        setDisplayMembers(sortedData);
        setSortConfig({ key, direction });
    };

    const getSortIcon = (key) => {
        if (sortConfig.key !== key) return <FaSort />;
        return sortConfig.direction === 'ascending' ? <FaSortUp /> : <FaSortDown />;
    };

    const committeeOptions = committees.map((committee) => ({
        id: committee.id,
        committeeType: committee.committeeName
    }));
    committeeOptions.push({ id: 0, committeeType: 'None' });

    return (
        <>
            <Page>
                <Container id="member-search-container" className="mt-3">
                    <Row className="mb-3 align-items-center">
                        <Col>
                            <h2>Members</h2>
                        </Col>
                        <Col className="text-end">
                            <Button
                                id="generate-report-button-search"
                                type={BUTTON_TYPES.primary}
                                buttonText="Generate Report"
                                action={() => openModal('generateReport')}
                            />
                        </Col>
                    </Row>

                    <div className="d-flex flex-row mb-3 align-items-center w-100">
                        <form className="d-flex flex-row justify-content-between align-items-center flex-nowrap w-100" onSubmit={handleSearchClick}>
                            <div className="d-flex flex-row flex-nowrap flex-grow-1">
                                <SearchInput
                                    id="search-bar"
                                    placeholder="Search by Member Name or RIT Email"
                                    value={searchQuery}
                                    onChange={handleSearchChange}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            search();
                                        }
                                    }}
                                />

                                <FilterButton
                                    id="filter-button"
                                    type={BUTTON_TYPES.secondary}
                                    action={(event) => {
                                        event.preventDefault();
                                        openModal('filterMembers');
                                    }}
                                />
                            </div>

                            <div className="col-auto">
                                <Button
                                    id="search-button"
                                    type={BUTTON_TYPES.primary}
                                    buttonText="Search"
                                    action={(event) => {
                                        event.preventDefault();
                                        search()
                                    }}
                                />
                            </div>
                        </form>
                    </div>

                    <div className="d-flex flex-row flex-wrap align-items-center mb-3" style={{ rowGap: '.5rem' }}>
                        <span className="me-3">Active Filters:</span>
                        {filterTags.map((tag, index) => (
                            <FilterTag
                                key={`${tag.field}-${tag.id || tag.label}`}
                                filterName={tag.label}
                                onRemove={() => removeFilter(tag.field, tag.id)}
                            />
                        ))}
                    </div>


                    <div>
                        <span className="text-start text-secondary mb-0">Total Members Found: {displayMembers.length}</span>
                    </div>

                    <Table striped bordered className="mt-0">
                        <thead>
                            <tr>
                                <th onClick={() => handleSort('firstName')}>
                                    Name {getSortIcon('firstName')}
                                </th>
                                <th onClick={() => handleSort('status')}>
                                    Status {getSortIcon('status')}
                                </th>
                                <th onClick={() => handleSort('activeMemberSemestersCount')}>
                                    Active Semesters {getSortIcon('activeMemberSemestersCount')}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {currentMembers.map((member, index) => (
                                <React.Fragment key={index}>
                                    <tr key={index} onClick={() => handleRowClick(index)}>
                                        <td>
                                            {member.firstName + " " + member.lastName + " (" + member.ritUserID + ")"}
                                            {member.archived && <span className="archived-red">{" (Archived)"}</span>}
                                        </td>
                                        <td className={member.status === "Active" ? "text-success" : "text-danger"}>{member.status}</td>
                                        <td className="d-flex justify-content-between align-items-center">
                                            {member.activeMemberSemestersCount >= member.manualSemesterCount ? member.activeMemberSemestersCount : member.manualSemesterCount}
                                            <span className="expand-icon">
                                                {expandedRows.includes(index) ? <FaChevronUp /> : <FaChevronDown />}
                                            </span>
                                        </td>
                                    </tr>
                                    {expandedRows.includes(index) && (
                                        <tr key={`expanded-${index}`}>
                                            <td colSpan="3">
                                                <div className="d-flex flex-row justify-content-between mt-5 mb-3 ms-3">
                                                    <div className="col-3"><b>RIT Email:</b> {member.ritEmail}</div>
                                                    <div className="col-3"><b>Pronouns:</b> {member.pronouns.map(pn => pn.dropdownValue).join(", ")}</div>
                                                    <div className="col-2"><b>General Meeting(s):</b> <span
                                                        onClick={() => goToEvents(member, "General Meetings")}
                                                        className="text-link"
                                                    >
                                                        {member.eventTypeAttendance["General Meeting"]}
                                                    </span>
                                                    </div>
                                                    <div className="col-4"><b>Committee(s):</b> {member.memberOfCommittees
                                                        .map(committeeName => committeeName)
                                                        .filter(name => name)
                                                        .join(", ")}</div>
                                                </div>
                                                <div className="d-flex flex-row justify-content-between mb-3 ms-3">
                                                    <div className="col-3"><b>Major:</b> {member.major.dropdownValue}</div>
                                                    <div className="col-3"><b>Gender:</b> {member.gender.dropdownValue}</div>
                                                    <div className="col-2"><b>Committee Meeting(s):</b> <span
                                                        onClick={() => goToEvents(member, "Committee Meetings")}
                                                        className="text-link"
                                                    >
                                                        {member.eventTypeAttendance["Committee Meeting"]}
                                                    </span>
                                                    </div>
                                                    <div className="col-4"><b>Specific Semesters Active:</b> {member.specificActiveSemesters}</div>
                                                </div>
                                                <div className="d-flex flex-row justify-content-between mb-3 ms-3">
                                                    <div className="col-3"><b>Phone Number:</b> {member.phoneNumber}</div>
                                                    <div className="col-3"><b>Race:</b> {member.race.dropdownValue}</div>
                                                    <div className="col-2"><b>Volunteer Event(s):</b> <span
                                                        onClick={() => goToEvents(member, "Volunteer Events")}
                                                        className="text-link"
                                                    >
                                                        {member.eventTypeAttendance["Volunteer Event"]}
                                                    </span>
                                                    </div>
                                                    <div className="col-4"><b>Previously Counted Semester:</b> {(member.manualSemesterCount && member.manualSemesterCount > member.activeMemberSemestersCount) ? (member.manualSemesterCount - member.activeMemberSemestersCount) : 0}</div>
                                                </div>
                                                <div className="d-flex flex-row justify-content-between mb-5 ms-3">
                                                    <div className="col-3"><b>Grad Year:</b> {member.gradYear}</div>
                                                    <div className="col-3"><b>T-Shirt Size:</b> {member.shirtSize.dropdownValue}</div>
                                                    <div className="col-2"><b>Social Event(s):</b> <span
                                                        onClick={() => goToEvents(member, "Social Events")}
                                                        className="text-link"
                                                    >
                                                        {member.eventTypeAttendance["Social Event"]}
                                                    </span>
                                                    </div>
                                                    <div className="col-4"></div>
                                                </div>
                                                <EditButton
                                                    id="edit-btn"
                                                    action={() => {
                                                        setSelectedMember(member);
                                                        openModal('editMember');
                                                    }}
                                                />
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            ))}
                        </tbody>
                    </Table>

                    <Pagination className="justify-content-center">
                        {Array.from({ length: Math.ceil(displayMembers.length / itemsPerPage) }, (_, i) => (
                            <Pagination.Item key={i + 1} active={i + 1 === currentPage} onClick={() => {
                                setCurrentPage(i + 1);
                                setExpandedRows([]);
                            }}>
                                {i + 1}
                            </Pagination.Item>
                        ))}
                    </Pagination>
                </Container>

                {activeModal === 'editMember' && (
                    <MemberEditModal
                        isOpen={true}
                        onClose={closeModal}
                        member={selectedMember}
                        initialFormData={modalFormData[selectedMember.id] || {}}
                        onFormDataChange={(updatedValues) =>
                            setModalFormData((prev) => ({
                                ...prev,
                                [selectedMember.id]: updatedValues
                            }))
                        }
                        updateMember={(displayMembers) => updateMembers(displayMembers)}
                    />
                )}
                {activeModal === 'filterMembers' && (
                    <MemberFilterModal
                        isOpen={true}
                        onClose={closeModal}
                        initialFormData={{
                            ...modalFormData.create,
                            selectedCommitteeTypeIDs: appliedFilters.selectedCommitteeTypeIDs
                        }}
                        onFormDataChange={(data) =>
                            setModalFormData(prev => ({ ...prev, create: data }))
                        }
                        onApply={(data) => handleFilterApply(data)}
                    />
                )}
                {activeModal === 'generateReport' && (
                    <GenerateReportModal
                        isOpen={true}
                        onClose={() => setActiveModal(null)}
                        formData={modalFormData.generate}
                        setFormData={(data) => setModalFormData(prev => ({ ...prev, generate: data }))}
                    />
                )}
            </Page>
        </>
    );
}